import React from "react";
import { TextField, MenuItem } from "@mui/material";
import { withStyles } from "@mui/styles";
import PropTypes from "prop-types";
import classNames from "classnames";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { getScrollBarAppState } from "orion-components/AppState/Global/selectors";

import map from "lodash/map";
import { useSelector } from "react-redux";

const styles = {
	root: {
		backgroundColor: "#2c2d2f"
	},
	icon: {
		color: "#B5B9BE"
	},
	iconRTL: {
		right: "unset!important",
		left: "0!important",
		background: "transparent",
		color: "#B5B9BE"
	},
	select: {
		paddingRight: "0!important",
		textAlign: "right"
	},
	underline: {
		"&:before": {
			borderBottom: "1px solid rgb(181, 185, 190)!important"
		},
		"&:after": {
			borderBottom: "2px solid rgb(22, 136, 189)"
		},
		marginTop: "12.5px!important"
	},
	selectBackground: {
		background: "transparent!important"
	},
	minimizedSelect: {
		background: "transparent!important",
		color: "#fff",
		fontSize: 10,
		height: "20px!important",
		padding: "5px!important"
	}
};

const propTypes = {
	classes: PropTypes.object.isRequired,
	formControlProps: PropTypes.object,
	id: PropTypes.string.isRequired,
	qaId: PropTypes.string,
	label: PropTypes.string,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.number, PropTypes.object]).isRequired,
	inputProps: PropTypes.object,
	items: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	handleChange: PropTypes.func.isRequired,
	open: PropTypes.bool,
	handleOpen: PropTypes.func,
	handleClose: PropTypes.func,
	controlled: PropTypes.bool,
	multiple: PropTypes.bool,
	maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	error: PropTypes.bool,
	helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	disabled: PropTypes.bool,
	dir: PropTypes.string,
	overrides: PropTypes.object,
	placeholder: PropTypes.string,
	customIcon: PropTypes.string,
	variant: PropTypes.string,
	minimizedSelect: PropTypes.bool,
	fieldSetBorderColor: PropTypes.string
};

const defaultProps = {
	label: "",
	items: [],
	children: [],
	open: false,
	handleOpen: () => {},
	handleClose: () => {},
	controlled: false,
	multiple: false,
	maxHeight: "auto",
	helperText: "",
	disabled: false,
	overrides: {},
	variant: "standard",
	fieldSetBorderColor: "#fff"
};

const CBSelectField = ({
	classes,
	formControlProps,
	id,
	qaId,
	label,
	value,
	inputProps,
	items,
	handleChange,
	children,
	open,
	handleOpen,
	handleClose,
	controlled,
	multiple,
	error,
	helperText,
	disabled,
	dir,
	overrides,
	placeholder,
	customIcon,
	variant,
	minimizedSelect,
	menuPaperProps,
	fieldSetBorderColor
}) => {
	const inlineStyles = {
		inputLabelProps: {
			whiteSpace: "nowrap",
			textOverflow: "ellipsis",
			...(dir === "rtl" && {
				width: "100%",
				transformOrigin: "top right",
				textAlign: "right"
			}),
			...(dir === "ltr" && {
				width: "90%",
				transformOrigin: "top left",
				textAlign: "left"
			}),
			color: "#B5B9BE",
			fontSize: 14,
			...(value && { transform: "translate(0px , -1.5px) scale(0.75)" })
		},
		formHelperTextProps: {
			...(dir === "rtl" && {
				position: "unset !important",
				textAlign: "right",
				opacity: "1 !important"
			})
		},
		menuItem: {
			display: "block",
			textOverflow: "ellipsis",
			...(dir === "ltr" && { textAlign: "left" }),
			...(dir === "rtl" && { textAlign: "right" })
		}
	};
	/**
	 * If Select Field is set to controlled, add necessary additional props
	 * onClose is mocked in order to control open state with passed handleChange function
	 */
	const controlProps = controlled
		? {
				open: open,
				onOpen: handleOpen,
				onClose: handleClose
			}
		: {};
	const scrollBarsEnabled = useSelector(state => getScrollBarAppState(state));
	return (
		// TODO: Potentially update to use FormControl and Select components
		<TextField
			{...formControlProps}
			id={id}
			data-testid={qaId ? qaId + "-dropdown" : ""}
			select
			disabled={disabled}
			variant={variant}
			label={label}
			error={error}
			onChange={handleChange}
			SelectProps={{
				IconComponent: customIcon === "more" ? MoreHorizIcon : customIcon === "none" ? null : ArrowDropDownIcon,
				classes:
					dir && dir == "rtl"
						? { icon: classes.iconRTL, select: minimizedSelect ? classes.minimizedSelect : classes.select }
						: {
								icon: classes.icon,
								select: minimizedSelect ? classes.minimizedSelect : classes.selectBackground
							},
				multiple,
				MenuProps: {
					anchorOrigin: {
						vertical: "top",
						horizontal: dir === "rtl" ? "right" : "left"
					},
					transformOrigin: {
						vertical: "top",
						horizontal: dir === "rtl" ? "right" : "left"
					},
					getContentAnchorEl: null,
					MenuListProps: {
						className: classNames(
							classes.root,
							minimizedSelect ? "cb-minimized-select-field-list" : "cb-select-field-list"
						)
					},
					PaperProps: {
						style: {
							width: "max-content",
							overflowY: scrollBarsEnabled ? "scroll" : "auto",
							...menuPaperProps
						},
						sx: {
							"& .MuiMenuItem-root:hover": {
								backgroundColor: "#3a3b3d"
							},
							"& .Mui-selected": {
								backgroundColor: "#4b6b86 !important"
							},
							"& .Mui-selected:hover": {
								backgroundColor: "#5c7488 !important"
							},
							"& .MuiMenuItem-root.Mui-focusVisible": {
								backgroundColor: "#3a3b3d"
							},
							"& .MuiMenuItem-root.Mui-selected.Mui-focusVisible": {
								backgroundColor: "#5c7488 !important"
							},
							"& .MuiMenuItem-root": {
								whiteSpace: "nowrap",
								textAlign: dir === "rtl" ? "right" : "left",
								direction: dir
							}
						}
					}
				},
				...controlProps
			}}
			InputProps={{
				...inputProps,
				classes: { underline: classes.underline }
			}}
			InputLabelProps={{
				style: inlineStyles.inputLabelProps
			}}
			margin="normal"
			fullWidth
			value={value}
			helperText={helperText}
			FormHelperTextProps={{
				style: inlineStyles.formHelperTextProps
			}}
			classes={{ root: overrides, input: classes.input }}
			placeholder={placeholder}
			sx={{
				"& .MuiOutlinedInput-root": {
					"& fieldset": {
						borderColor: fieldSetBorderColor
					},
					"&:hover fieldset": {
						borderColor: fieldSetBorderColor
					}
				}
			}}
		>
			{children}
			{map(items, item => (
				// display: "block" solves text-overflow: "ellipsis" conflict with flex (https://github.com/mui-org/material-ui/issues/11380)
				<MenuItem key={item.id} value={item.id} style={inlineStyles.menuItem} data-testid="orion-components-cbcomponents-cbselect-field-cbselect-field-menu-item">
					{item.name || item.value}
				</MenuItem>
			))}
		</TextField>
	);
};

CBSelectField.propTypes = propTypes;
CBSelectField.defaultProps = defaultProps;

export default withStyles(styles)(CBSelectField);
